import { useState } from 'react'
import { Button, Col, Form, Image } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  useImgAltLogin,
  useImgAltLogo,
  useImgSrcLogin,
  useImgSrcLogo
} from '../../store'
import { ConfirmModal } from '../confirm'
import { ColorInput } from './ColorInput'
import { ImageDrop } from './ImageDrop'
import { InfoButton } from './InfoButton'
import { DEFAULT_ASSETS } from '../../pages/layout/AssetsProvider'

const INITIAL_DATA = {
  deleteLogoImage: null,
  deleteLoginImage: null,
  logoImageContent: null,
  isLogoImageOverridden: false,
  logoAltText: null,
  loginImageContent: null,
  isLoginImageOverridden: false,
  loginAltText: null,
  primaryColor: '#3F4443',
  secondaryColor: '#BA0C2F'
}

yup.addMethod(
  yup.object,
  'imageAltTextPairRequired',
  function (imageFieldName, altTextFieldName, isOverriddenFieldName, message) {
    return this.test('imageAltTextPairRequired', message, function (values) {
      const imageValue = values[imageFieldName]
      const altTextValue = values[altTextFieldName]
      const isImageOverridden = values[isOverriddenFieldName]

      const isImagePresent = imageValue instanceof File
      const isAltTextMeaningful = altTextValue && altTextValue.trim().length > 0
      const hasImageOrWasOverridden = isImagePresent || isImageOverridden

      if (hasImageOrWasOverridden && !isAltTextMeaningful) {
        return this.createError({ path: altTextFieldName, message })
      }

      if (isAltTextMeaningful && !hasImageOrWasOverridden) {
        return this.createError({ path: imageFieldName, message })
      }

      return true
    })
  }
)

const validationSchema = yup
  .object({
    logoImageContent: yup.mixed(),
    isLogoImageOverridden: yup.boolean().default(false),
    logoAltText: yup
      .string()
      .nullable()
      .max(255, 'Alt Text for the Logo cannot be more than 255 characters'),
    loginImageContent: yup.mixed(),
    isLoginImageOverridden: yup.boolean().default(false),
    loginAltText: yup
      .string()
      .nullable()
      .max(
        255,
        'Alt Text for the Login Image cannot be more than 255 characters'
      ),
    primaryColor: yup
      .string()
      .nullable()
      .matches('^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$', {
        excludeEmptyString: true,
        message:
          'Hexadecimal colors must be #RRGGBB or #RGB containing only characters (0-9, A-F)'
      }),
    secondaryColor: yup
      .string()
      .nullable()
      .matches('^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$', {
        excludeEmptyString: true,
        message:
          'Hexadecimal colors must be #RRGGBB or #RGB containing only characters (0-9, A-F)'
      })
  })
  .imageAltTextPairRequired(
    'logoImageContent',
    'logoAltText',
    'isLogoImageOverridden',
    'Project Logo and Alt Text must both be provided.'
  )
  .imageAltTextPairRequired(
    'loginImageContent',
    'loginAltText',
    'isLoginImageOverridden',
    'Login Image and Alt Text must both be provided.'
  )

/** form to create or update the tenant */
const TenantBrandingForm = ({ formData = INITIAL_DATA, onSubmit }) => {
  const MODAL = {
    LOGO_INFO: {
      alert: true,
      title: 'What is a Project Logo?',
      body: (
        <>
          <p>
            The project logo helps users quickly recognize your project. It
            appears in the following places:
          </p>
          <ul>
            <li>On the Landing (Login) page</li>
            <li>At the top of every page, next to the page title</li>
          </ul>
          <p>
            Use a simple, recognizable image, such as your project or department
            logo.
          </p>
          <Image
            fluid
            src='/help-logo.png'
            alt='Diagram showing placement of the Project Logo on the landing page and in the top banner of internal pages within the portal.'
          />
        </>
      )
    },
    LOGIN_INFO: {
      alert: true,
      title: 'What is a Login Image?',
      body: (
        <>
          <p>
            The login image helps visually introduce your project to users. It
            appears in the following places:
          </p>
          <ul>
            <li>On the Landing (Login) page</li>
            <li>On the first screen of Page Tooltips</li>
          </ul>
          <p>
            Use a simple, welcoming image that reflects your project. A wide
            image works best for this space.
          </p>
          <Image
            fluid
            src='/help-login.png'
            alt='Diagram showing the Login Image placement on the landing page and how it appears on the page tooltip welcome screen in the portal.'
          />
        </>
      )
    },
    ALT_INFO: {
      alert: true,
      title: 'What is Alt Text?',
      body: (
        <>
          <p>
            Alt text (short for alternative text) is a short description of the
            image that helps people using screen readers understand what the
            image shows.
          </p>
          <p>
            <strong>Example: </strong>For a logo that says “Healthy Heart
            Program,” the alt text could be:{' '}
            <strong>“Healthy Heart Program logo”.</strong>
          </p>
          <p>
            If the image is decorative and doesn’t convey meaningful content,
            type: <strong>“Decorative”.</strong>
          </p>
        </>
      )
    },
    RESET_IMAGE: {
      title: 'Are you sure you want to reset this image to the default?',
      body: (
        <>
          <p>
            Once reset, this image will no longer appear on your portal and will
            be replaced with the default image.
          </p>
          <p>You can upload a new image at any time.</p>
        </>
      )
    }
  }
  const imgSrcLogo = useImgSrcLogo()
  const imgAltLogo = useImgAltLogo()
  const imgSrcLogin = useImgSrcLogin()
  const imgAltLogin = useImgAltLogin()
  const [logoImgSrc, setLogoImgSrc] = useState(imgSrcLogo)
  const [loginImgSrc, setLoginImgSrc] = useState(imgSrcLogin)
  const [modal, setModal] = useState(MODAL.LOGO_INFO)
  const [showModal, setShowModal] = useState(false)

  const defaultValues = {
    ...formData,
    isLogoImageOverridden: imgSrcLogo !== DEFAULT_ASSETS.imgSrcLogo,
    isLoginImageOverridden: imgSrcLogin !== DEFAULT_ASSETS.imgSrcLogin,
    primaryColor: formData.primaryColor || INITIAL_DATA.primaryColor,
    secondaryColor: formData.secondaryColor || INITIAL_DATA.secondaryColor
  }

  if (imgAltLogo === '') {
    defaultValues.logoAltText = 'Decorative'
  } else if (imgAltLogo !== DEFAULT_ASSETS.imgAltLogo) {
    defaultValues.logoAltText = imgAltLogo
  } else {
    defaultValues.logoAltText = null
  }

  if (imgAltLogin === '') {
    defaultValues.loginAltText = 'Decorative'
  } else if (imgAltLogin !== DEFAULT_ASSETS.imgAltLogin) {
    defaultValues.loginAltText = imgAltLogin
  } else {
    defaultValues.loginAltText = null
  }

  const methods = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(validationSchema)
  })
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = methods

  const handleLogoInfoClick = () => {
    setModal(MODAL.LOGO_INFO)
    setShowModal(true)
  }

  const handleLoginInfoClick = () => {
    setModal(MODAL.LOGIN_INFO)
    setShowModal(true)
  }

  const handleAltInfoClick = () => {
    setModal(MODAL.ALT_INFO)
    setShowModal(true)
  }

  const handleResetLogoClick = () => {
    setModal({
      ...MODAL.RESET_IMAGE,
      onConfirm: () => {
        setValue('deleteLogoImage', true, { shouldDirty: true })
        setValue('logoImageContent', undefined, { shouldDirty: true })
        setValue('isLogoImageOverridden', false, { shouldDirty: true })
        setValue('logoAltText', undefined, { shouldDirty: true })
        setLogoImgSrc(DEFAULT_ASSETS.imgSrcLogo)
        setShowModal(false)
      }
    })
    setShowModal(true)
  }

  const handleResetLoginClick = () => {
    setModal({
      ...MODAL.RESET_IMAGE,
      onConfirm: () => {
        setValue('deleteLoginImage', true, { shouldDirty: true })
        setValue('loginImageContent', undefined, { shouldDirty: true })
        setValue('isLoginImageOverridden', false, { shouldDirty: true })
        setValue('loginAltText', undefined, { shouldDirty: true })
        setLoginImgSrc(DEFAULT_ASSETS.imgSrcLogin)
        setShowModal(false)
      }
    })
    setShowModal(true)
  }

  const onDiscard = () => {
    reset(defaultValues)
    setLogoImgSrc(imgSrcLogo)
    setLoginImgSrc(imgSrcLogin)
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit((data) => onSubmit(data))}>
        <Form.Row className='mb-4'>
          <Col lg={6} md={8} className='pr-lg-4 mb-4'>
            <ImageDrop
              controlId='logoImageContent'
              label='Project Logo'
              onInfoClick={handleLogoInfoClick}
              imgSrc={logoImgSrc}
              helperText='Recommended size: 200x200px, Max file size: 2MB'
              maxSize={2 * 1048576}
            />
            <Button
              variant='link'
              onClick={handleResetLogoClick}
              className='p-0 mt-n1 mb-4'
            >
              Reset Logo Image to Default
            </Button>
            <Form.Group controlId='logoAltText'>
              <Form.Label className='font-weight-bold'>
                Alt Text for the Logo
              </Form.Label>
              <InfoButton
                label='Alt Text for the Logo Info'
                onClick={handleAltInfoClick}
              />
              <Form.Control
                type='text'
                placeholder='Add Alt Text for the Image'
                {...register('logoAltText')}
                isInvalid={!!errors.logoAltText}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.logoAltText?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col lg={6} md={8} className='pl-lg-4 mb-4'>
            <ImageDrop
              controlId='loginImageContent'
              label='Login Image'
              onInfoClick={handleLoginInfoClick}
              imgSrc={loginImgSrc}
              helperText='Recommended size: 1200x300px, Max file size: 5MB'
              maxSize={5 * 1048576}
            />
            <Button
              variant='link'
              onClick={handleResetLoginClick}
              className='p-0 mt-n1 mb-4'
            >
              Reset Login Image to Default
            </Button>
            <Form.Group controlId='loginAltText'>
              <Form.Label className='font-weight-bold'>
                Alt Text for the Login Image
                <InfoButton
                  label='Alt Text for the Login Image Info'
                  onClick={handleAltInfoClick}
                />
              </Form.Label>
              <Form.Control
                type='text'
                placeholder='Add Alt Text for the Image'
                {...register('loginAltText')}
                isInvalid={!!errors.loginAltText}
              />
              <Form.Control.Feedback type='invalid'>
                {errors.loginAltText?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Form.Row>
        <Form.Row className='mb-5'>
          <Col lg={6} md={8} className='pr-lg-4 mb-5'>
            <ColorInput controlId='primaryColor' label='Primary Color' />
          </Col>
          <Col lg={6} md={8} className='pl-lg-4 mb-5'>
            <ColorInput controlId='secondaryColor' label='Secondary Color' />
          </Col>
        </Form.Row>
        <Form.Row>
          <Col className='d-flex justify-content-end'>
            <Button variant='secondary' type='submit' disabled={!isDirty}>
              Save Changes
            </Button>
            <Button variant='outline-dark' onClick={onDiscard} className='ml-3'>
              Discard
            </Button>
          </Col>
        </Form.Row>
      </Form>
      <ConfirmModal
        size='lg'
        alert={modal.alert}
        show={showModal}
        title={modal.title}
        body={modal.body}
        onConfirm={modal.onConfirm}
        onCancel={() => setShowModal(false)}
      />
    </FormProvider>
  )
}

export { TenantBrandingForm }
